import type {
  ColDef,
  ICellRendererParams,
  ISetFilterParams,
  ValueGetterParams,
} from 'ag-grid-community'

import { AIPosture } from '@/gen/inventory/v1/ai_posture_pb'

import { aiPostureLabel } from '@/const/label'
import { sortAIPosture } from '@/const/priority'

import { AI_POSTURES } from '@/lib/connect/proto-types'

import { AIPostureBadge } from './ai-posture-badge'

// UNSPECIFIED is excluded here; rows with an UNSPECIFIED or missing posture fold
// into NON_AI ("AI not detected") via the column's valueGetter (or filterValueGetter
// on the filter-only variant below).
export const AI_POSTURE_FILTER_VALUES = AI_POSTURES.map(
  ({ number }) => aiPostureLabel[number as AIPosture],
)

// AI_POSTURE_FILTER_VALUES minus "AI not detected" — the drill-down bucket for the
// AI-governance charts that only count model-provider / AI-native / embedded-AI vendors.
export const AI_ONLY_POSTURE_LABELS = AI_POSTURE_FILTER_VALUES.filter(
  (label) => label !== aiPostureLabel[AIPosture.AI_POSTURE_NON_AI],
)

type AIPostureColumnOptions<TRow> = {
  colId: string
  headerName: string
  flex?: number
  minWidth?: number
  hide?: boolean
  headerClass?: string
  getPosture: (row: TRow | undefined) => AIPosture | undefined
  // Replaces the read-only badge cell. Everything the grid sorts and filters on — valueGetter,
  // comparator, filterParams — stays owned by this factory, so an editable cell cannot drift
  // from the read-only ones. Kept as an option rather than an `editable` flag so this component
  // never has to import a page's cell: shared/ must not depend on pages/.
  cellRenderer?: ColDef<TRow>['cellRenderer']
}

// One canonical AI Posture column def — badge cell renderer, agSetColumnFilter
// over the fixed labels, priority-based sort. Reused across every table that
// surfaces AI Posture.
export const aiPostureColumnDef = <TRow,>({
  colId,
  headerName,
  flex,
  minWidth,
  hide,
  headerClass,
  getPosture,
  cellRenderer,
}: AIPostureColumnOptions<TRow>): ColDef<TRow> => ({
  colId,
  headerName,
  flex,
  minWidth,
  hide,
  headerClass,
  sortable: true,
  cellRenderer:
    cellRenderer ??
    ((params: ICellRendererParams<TRow>) => (
      <AIPostureBadge posture={getPosture(params.data)} displayVariant='column' />
    )),
  filter: 'agSetColumnFilter',
  filterParams: {
    values: AI_POSTURE_FILTER_VALUES,
  } as ISetFilterParams,
  comparator: (_valueA, _valueB, nodeA, nodeB) =>
    sortAIPosture(
      getPosture(nodeA?.data ?? undefined) ?? AIPosture.AI_POSTURE_UNSPECIFIED,
      getPosture(nodeB?.data ?? undefined) ?? AIPosture.AI_POSTURE_UNSPECIFIED,
    ),
  valueGetter: (params: ValueGetterParams<TRow>) =>
    aiPostureLabel[getPosture(params.data ?? undefined) || AIPosture.AI_POSTURE_NON_AI],
})

type AIPostureFilterOnlyColumnOptions<TRow> = {
  colId: string
  headerName: string
  getPosture: (row: TRow | undefined) => AIPosture | undefined
}

// Filter-only AI Posture column: hidden from the grid and the "add column" popup.
// UNSPECIFIED (or missing row) folds into NON_AI so the "AI not detected" bucket
// matches the AI-governance charts.
export const aiPostureFilterOnlyColumnDef = <TRow,>({
  colId,
  headerName,
  getPosture,
}: AIPostureFilterOnlyColumnOptions<TRow>): ColDef<TRow> => ({
  colId,
  headerName,
  hide: true,
  suppressColumnsToolPanel: true,
  sortable: false,
  filter: 'agSetColumnFilter',
  filterParams: {
    values: AI_POSTURE_FILTER_VALUES,
    suppressMiniFilter: true,
    selectAllOnMiniFilter: true,
  } as ISetFilterParams,
  filterValueGetter: (params: ValueGetterParams<TRow>) =>
    aiPostureLabel[getPosture(params.data ?? undefined) || AIPosture.AI_POSTURE_NON_AI],
})
